import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import { useThemeMode } from "../../context/ThemeContext";
import { useAuth0 } from "@auth0/auth0-react";

interface AppBarProps extends MuiAppBarProps {
  drawerWidth?: number;
}

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "drawerWidth",
})<AppBarProps>(({ theme, drawerWidth }) => {
  const isDarkMode = theme.palette.mode === "dark";

  return {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: isDarkMode
      ? "0 4px 12px rgba(0, 0, 0, 0.45)"
      : "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
    background: isDarkMode
      ? `linear-gradient(90deg, #1a237e 0%, ${theme.palette.primary.dark} 100%)`
      : `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    [theme.breakpoints.up("md")]: {
      width: drawerWidth ? `calc(100% - ${drawerWidth}px)` : "100%",
      marginLeft: drawerWidth ? `${drawerWidth}px` : 0,
    },
  };
});

const Logo = styled("img")({
  height: 36,
  marginRight: 10,
});

interface CustomAppBarProps {
  drawerWidth: number;
  onDrawerToggle: () => void;
}

const AppBar: React.FC<CustomAppBarProps> = ({
  drawerWidth,
  onDrawerToggle,
}) => {
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // Auth0 hooks
  const { isAuthenticated, user, loginWithRedirect, logout } = useAuth0();

  // Profile menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogin = () => {
    loginWithRedirect({
      appState: {
        returnTo: window.location.pathname,
      },
    });
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    handleProfileMenuClose();
  };

  const handleProfileClick = () => {
    navigate("/profile");
    handleProfileMenuClose();
  };

  const handleSettingsClick = () => {
    navigate("/settings");
    handleProfileMenuClose();
  };

  return (
    <StyledAppBar position="fixed" drawerWidth={drawerWidth}>
      <Toolbar>
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <Logo
            src={mode === "dark" ? "/logo.svg" : "/logo-light.svg"}
            alt="Promptfoo Manager"
          />
          <Typography variant="h6" noWrap component="div">
            Promptfoo Manager
          </Typography>
        </Box>

        {/* Icons */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip
            title={
              mode === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"
            }
          >
            <IconButton color="inherit" onClick={toggleMode}>
              {mode === "light" ? <Brightness4Icon /> : <Brightness7Icon />}
            </IconButton>
          </Tooltip>

          {/* Authentication */}
          {isAuthenticated ? (
            <>
              <Tooltip title="Logout">
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  size="small"
                  sx={{
                    ml: 2,
                    mr: 1,
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    color: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.8)",
                      backgroundColor: "rgba(255, 0, 0, 0.1)",
                    },
                    textTransform: "none",
                    minWidth: { xs: "40px", sm: "auto" },
                  }}
                >
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    Logout
                  </Box>
                </Button>
              </Tooltip>

              <Tooltip title="Account">
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  sx={{ ml: 1 }}
                  aria-controls={open ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                >
                  {user?.picture ? (
                    <Avatar
                      src={user.picture}
                      alt={user.name || "Profile"}
                      sx={{ width: 32, height: 32 }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: theme.palette.primary.main,
                      }}
                    >
                      {user?.name?.charAt(0) || user?.email?.charAt(0) || "U"}
                    </Avatar>
                  )}
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleProfileMenuClose}
                onClick={handleProfileMenuClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                    mt: 1.5,
                    "& .MuiAvatar-root": {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {user?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                <MenuItem onClick={handleSettingsClick}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<LoginIcon />}
              onClick={handleLogin}
              sx={{
                ml: 2,
                borderColor: "rgba(255, 255, 255, 0.5)",
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.8)",
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
                textTransform: "none",
              }}
            >
              Login
            </Button>
          )}
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};

export default AppBar;
